import { useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { JobStatus } from '@wrapforce/shared';
import { PLAN_DEFINITIONS } from '@wrapforce/shared/constants';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useMembers } from '@/features/workspace/api';
import { useLocations } from '@/features/locations/api';
import { useAssignJob, useJob, useJobEvents, useRecordPayment, useUpdateJobStatus } from './api';
import { SchedulingFlow } from './SchedulingFlow';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { MultiSelect } from '@/components/ui/multi-select';
import { useLocaleFormatters } from '@/lib/format';

// Mirrors server/src/modules/jobs/state.ts for UI affordances only - the
// server remains the enforcer (§10.3).
const NEXT_STATES: Record<JobStatus, JobStatus[]> = {
  unscheduled: ['scheduled', 'cancelled'],
  scheduled: ['in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: ['closed'],
  closed: [],
  cancelled: [],
};

export function JobDetailPage() {
  const { t } = useTranslation('jobs');
  const { formatMoney, formatDateTime } = useLocaleFormatters();
  const { jobId } = useParams<{ jobId: string }>();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const isInstaller = membership?.role === 'installer';
  const isManager = membership?.role === 'owner' || membership?.role === 'manager' || membership?.role === 'front_desk';

  const gateOn = membership ? PLAN_DEFINITIONS[membership.plan].gates.multiLocation : false;

  const { data: job } = useJob(workspaceId, jobId);
  const { data: events = [] } = useJobEvents(workspaceId, jobId);
  const { data: members = [] } = useMembers(workspaceId);
  const { data: locationsData } = useLocations(workspaceId);
  const updateStatus = useUpdateJobStatus(workspaceId, jobId ?? '');
  const assignJob = useAssignJob(workspaceId, jobId ?? '');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const { confirm, ConfirmDialog } = useConfirm();

  if (!job) return null;

  const nextStates = NEXT_STATES[job.status];
  const location = locationsData?.items.find((l) => l.id === job.locationId);

  async function handleMarkStatus(s: JobStatus) {
    const ok = await confirm({
      title: t('confirm.markStatus.title', { status: t(`status.job.${s}`) }),
      description: t('confirm.markStatus.description', { status: t(`status.job.${s}`) }),
      confirmLabel: t('common:actions.confirm'),
      cancelLabel: t('common:actions.cancel'),
      variant: s === 'cancelled' ? 'danger' : 'primary',
    });
    if (!ok) return;
    updateStatus.mutate(s);
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
      <ConfirmDialog />
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-xl font-bold">J-{job.number}</h1>
        <span className="bg-surface-2 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-muted">{t(`status.job.${job.status}`)}</span>
        {gateOn && location ? (
          <span className="flex items-center gap-1 border border-border px-2 py-0.75 text-[11px] font-medium text-muted">
            <MapPin size={11} /> {location.name}
          </span>
        ) : null}
        <div className="flex flex-wrap gap-2 md:ml-auto">
          {nextStates.map((s) => (
            <Button key={s} size="sm" variant={s === 'cancelled' ? 'outline' : 'primary'} onClick={() => handleMarkStatus(s)}>
              {t('actions.markStatus', { status: t(`status.job.${s}`) })}
            </Button>
          ))}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{job.customerName} · {job.vehicleLabel}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-1.5">
          {job.lineItems.map((li, i) => (
            <div key={i} className="text-sm text-ink">
              {li.serviceName} · {li.coverageName} ({li.materialBrand} {li.materialSeries})
            </div>
          ))}
        </CardContent>
      </Card>

      {isManager && jobId ? <SchedulingFlow workspaceId={workspaceId} jobId={jobId} job={job} /> : null}

      {isManager ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('card.assign')}</CardTitle>
          </CardHeader>
          <CardContent>
            <MultiSelect
              values={job.assignedInstallers.map((a) => a.id)}
              onChange={(installerIds) => assignJob.mutate({ installerIds })}
              options={members.map((m) => ({ value: m.userId, label: m.fullName }))}
            />
            {job.assignedInstallers.length ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {job.assignedInstallers.map((a) => (
                  <span key={a.id} className="bg-surface-2 px-2 py-0.5 text-xs text-ink">{a.fullName}</span>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {!isInstaller && job.totalCents !== undefined ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('card.payments')}</CardTitle>
            <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setPaymentOpen(true)}>
              {t('actions.recordPayment')}
            </Button>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            {(job.payments ?? []).map((p) => (
              <div key={p.id} className="flex justify-between text-sm">
                <span className="capitalize text-ink">{p.kind === 'stripe_deposit' ? t('payment.stripeDeposit') : t('payment.manual', { method: t(`paymentMethod.${p.method}`) })}</span>
                <span className="font-mono">{formatMoney(p.amountCents)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-border pt-2 text-base font-bold">
              <span>{t('balanceDue')}</span>
              <span className="font-mono">{formatMoney(job.balanceCents ?? 0)}</span>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('card.history')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2.5">
          {events.map((e) => (
            <div key={e.id} className="border-l-2 border-border pl-3 text-sm">
              <div className="text-ink">{e.message}</div>
              <div className="text-xs text-muted">{formatDateTime(new Date(e.createdAt))}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      {workspaceId && jobId ? <RecordPaymentDialog open={paymentOpen} onOpenChange={setPaymentOpen} workspaceId={workspaceId} jobId={jobId} /> : null}
    </div>
  );
}

function RecordPaymentDialog({ open, onOpenChange, workspaceId, jobId }: { open: boolean; onOpenChange: (v: boolean) => void; workspaceId: string; jobId: string }) {
  const { t } = useTranslation('jobs');
  const recordPayment = useRecordPayment(workspaceId, jobId);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<'cash' | 'etransfer' | 'card' | 'other'>('cash');

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('dialog.recordPaymentTitle')}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="amount">{t('form.amount')}</Label>
          <Input id="amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>{t('form.method')}</Label>
          <select value={method} onChange={(e) => setMethod(e.target.value as typeof method)} className="h-10 rounded-[var(--radius-input)] border border-border bg-surface px-3 text-sm">
            <option value="cash">{t('paymentMethod.cash')}</option>
            <option value="etransfer">{t('paymentMethod.etransfer')}</option>
            <option value="card">{t('paymentMethod.card')}</option>
            <option value="other">{t('paymentMethod.other')}</option>
          </select>
        </div>
        <Button
          disabled={!amount || Number(amount) <= 0}
          loading={recordPayment.isPending}
          onClick={async () => {
            await recordPayment.mutateAsync({ amountCents: Math.round(Number(amount) * 100), method });
            toast.success(t('toast.paymentRecorded'));
            onOpenChange(false);
          }}
        >
          {t('actions.recordPayment')}
        </Button>
      </div>
    </Dialog>
  );
}
